import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import api from '../../api/axios';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/settings');
      setSettings(response.data);
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(settings.map(setting => 
      setting.setting_key === key 
        ? { ...setting, setting_value: value }
        : setting
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError(null);

    try {
      for (const setting of settings) {
        await api.put(`/api/admin/settings/${setting.setting_key}`, {
          value: setting.setting_value
        });
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
      console.error('Failed to update settings:', err);
    }
  };

  const getSettingInput = (setting) => {
    if (setting.setting_type === 'text' && setting.setting_key.includes('email')) {
      return (
        <Form.Control
          as="textarea"
          rows={2}
          value={setting.setting_value || ''}
          onChange={(e) => handleChange(setting.setting_key, e.target.value)}
          placeholder="Comma-separated email addresses"
        />
      );
    } else if (setting.setting_type === 'text') {
      return (
        <Form.Control
          type="text"
          value={setting.setting_value || ''}
          onChange={(e) => handleChange(setting.setting_key, e.target.value)}
        />
      );
    } else {
      return (
        <Form.Control
          type="text"
          value={setting.setting_value || ''}
          onChange={(e) => handleChange(setting.setting_key, e.target.value)}
        />
      );
    }
  };

  const formatLabel = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">{t('admin.settings')}</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{t('common.success')}</Alert>}
      
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <h5 className="mb-3">{t('admin.landingSettings')}</h5>
            
            {settings
              .filter(s => s.setting_key.startsWith('landing_page'))
              .map((setting) => (
                <Form.Group className="mb-3" key={setting.id}>
                  <Form.Label>
                    <strong>{formatLabel(setting.setting_key)}</strong>
                  </Form.Label>
                  {getSettingInput(setting)}
                </Form.Group>
              ))}

            <hr className="my-4" />

            <h5 className="mb-3">{t('admin.emailSettings')}</h5>
            
            {settings
              .filter(s => s.setting_key.includes('email') || s.setting_key.includes('order'))
              .map((setting) => (
                <Form.Group className="mb-3" key={setting.id}>
                  <Form.Label>
                    <strong>{formatLabel(setting.setting_key)}</strong>
                  </Form.Label>
                  {getSettingInput(setting)}
                  {setting.setting_key === 'order_notification_emails' && (
                    <Form.Text className="text-muted">
                      Add comma-separated email addresses to receive order notifications
                    </Form.Text>
                  )}
                  {setting.setting_key === 'order_email_template' && (
                    <Form.Text className="text-muted">
                      This text will be included in order confirmation emails
                    </Form.Text>
                  )}
                </Form.Group>
              ))}

            <div className="d-grid gap-2 mt-4">
              <Button variant="primary" type="submit" size="lg">
                {t('common.save')}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminSettings;
